import { css } from '@emotion/react';
import { useCallback, useEffect, useState } from 'react';

/** Figma 디자인 기준 폭 */
export const DESIGN_WIDTH = 375;

/** 768px 이상에서 px 고정 */
export const TABLET_MIN = 768;

export const tabletUp = `@media (min-width: ${TABLET_MIN}px)`;

/** 375px 디자인 기준 px → vw */
export function vw(px: number): string {
  return `${parseFloat(((px / DESIGN_WIDTH) * 100).toFixed(4))}vw`;
}

/** 뷰포트 폭에 따라 vw 또는 px 문자열 반환 (JS inline style/transform용) */
export function lengthPx(px: number, viewportWidth: number): string {
  return viewportWidth < TABLET_MIN ? vw(px) : `${px}px`;
}

/** JS에서 반응형 길이 단위 (캐러셀 transform 등) */
export function useResponsiveLength() {
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : DESIGN_WIDTH,
  );

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return useCallback((px: number) => lengthPx(px, viewportWidth), [viewportWidth]);
}

type ResponsiveProp =
  | 'width'
  | 'height'
  | 'min-width'
  | 'min-height'
  | 'max-width'
  | 'max-height'
  | 'top'
  | 'right'
  | 'bottom'
  | 'left'
  | 'padding'
  | 'margin-top'
  | 'margin-right'
  | 'margin-bottom'
  | 'margin-left'
  | 'padding-top'
  | 'padding-right'
  | 'padding-bottom'
  | 'padding-left'
  | 'gap'
  | 'font-size'
  | 'line-height'
  | 'border-radius'
  | 'outline-offset'
  | 'flex-basis';

/** styled: ${r('height', 54)} — 모바일 vw, 768px+ px */
export function r(prop: ResponsiveProp, px: number) {
  return css`
    ${prop}: ${vw(px)};
    @media (min-width: ${TABLET_MIN}px) {
      ${prop}: ${px}px;
    }
  `;
}

/** 동적 px prop: width: ${({ $w }) => rv($w, 'width')} */
export function rv(px: number, prop: ResponsiveProp) {
  return css`
    ${prop}: ${vw(px)};
    @media (min-width: ${TABLET_MIN}px) {
      ${prop}: ${px}px;
    }
  `;
}

/** shorthand padding/margin (4값 동일 px) */
export function rInset(
  prop: 'padding' | 'margin',
  top: number,
  right?: number,
  bottom?: number,
  left?: number,
) {
  const rVal = right ?? top;
  const bVal = bottom ?? top;
  const lVal = left ?? rVal;
  const mobile = `${vw(top)} ${vw(rVal)} ${vw(bVal)} ${vw(lVal)}`;
  const desktop = `${top}px ${rVal}px ${bVal}px ${lVal}px`;
  return css`
    ${prop}: ${mobile};
    @media (min-width: ${TABLET_MIN}px) {
      ${prop}: ${desktop};
    }
  `;
}
